import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Vault, ShieldCheck, Users, Lock } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@familyvault.in");
  const [password, setPassword] = useState("Demo@12345");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left — form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center">
              <Vault className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading font-semibold text-slate-900 leading-tight">
                Family Vault
              </div>
              <div className="label-overline">Emergency Continuity</div>
            </div>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Welcome back.
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Your family's financial continuity — secured in one place.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={busy}
              data-testid="login-submit-button">
              {busy ? "Signing in..." : "Sign in securely"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            Don't have a vault yet?{" "}
            <Link to="/register" className="text-slate-900 font-medium underline underline-offset-4"
              data-testid="link-to-register">
              Create an account
            </Link>
          </p>

          <Card className="mt-8 p-4 border-slate-200 bg-slate-50/50">
            <div className="text-xs font-medium text-slate-600 mb-1">Demo credentials</div>
            <div className="text-xs text-slate-500 font-mono">demo@familyvault.in · Demo@12345</div>
          </Card>
        </div>
      </div>

      {/* Right — secure imagery */}
      <div className="hidden lg:block relative overflow-hidden bg-slate-900">
        <img
          alt="Secure vault"
          src="https://images.pexels.com/photos/14866072/pexels-photo-14866072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/80 to-slate-900/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="max-w-md">
            <div className="label-overline text-slate-300">Built for families</div>
            <h2 className="font-heading text-4xl xl:text-5xl font-semibold tracking-tight mt-4 leading-tight">
              If something happens to you — your family won't be lost.
            </h2>
            <p className="text-slate-300 mt-4 text-base">
              A single, serious place to organise banks, investments, insurance,
              lockers, nominees and the first steps your loved ones need.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 max-w-md">
            <div>
              <ShieldCheck className="h-5 w-5 text-sky-400 mb-2" strokeWidth={1.75} />
              <div className="text-sm font-medium">Encrypted at rest</div>
              <div className="text-xs text-slate-400">PAN, Aadhaar, policy numbers</div>
            </div>
            <div>
              <Lock className="h-5 w-5 text-sky-400 mb-2" strokeWidth={1.75} />
              <div className="text-sm font-medium">Masked by default</div>
              <div className="text-xs text-slate-400">Reveal only when needed</div>
            </div>
            <div>
              <Users className="h-5 w-5 text-sky-400 mb-2" strokeWidth={1.75} />
              <div className="text-sm font-medium">Emergency access</div>
              <div className="text-xs text-slate-400">Approve, log, revoke</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
